import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import reportService, { PatientContextPayload, ReportResponse, UpdateReportRequest } from '../services/reportService';
import apiClient from '../services/apiClient';

interface ReportState {
  report: ReportResponse | null;
  loading: boolean;
  error: string | null;
  isEditing: boolean;
  isSaving: boolean;
  isFinalized: boolean;
  // Backend configuration fetched from API
  config: {
    hardLockEnabled: boolean;
    exportTheme: string;
    gradingEnabled: boolean;
    gradingEnforcementMode: string;
  } | null;
  // Timestamp of last config fetch for freshness checks (milliseconds since epoch)
  configFetchedAt: number | null;
  // Separate error state for config fetch to show non-blocking warnings
  configError: string | null;
}

const initialState: ReportState = {
  report: null,
  loading: false,
  error: null,
  isEditing: false,
  isSaving: false,
  isFinalized: false,
  // Pre-populated with safe defaults so selectIsReportEditable never
  // transiently returns the wrong value before fetchReportConfig completes.
  config: {
    hardLockEnabled: false,
    exportTheme: 'blue',
    gradingEnabled: false,
    gradingEnforcementMode: 'warn',
  },
  configFetchedAt: null,
  configError: null,
};

export const generateReport = createAsyncThunk(
  'report/generate',
  async (
    { sessionId, clinicalHistory, patientContext, cptCode, cptExamName, cptDetectionMethod, cptModality, cptBodyRegion, cptLaterality, cptContrast, cptDetectionConfidence, customTemplate }: {
      sessionId: string;
      clinicalHistory?: string;
      patientContext?: PatientContextPayload;
      cptCode?: string;
      cptExamName?: string;
      cptDetectionMethod?: string;
      cptModality?: string;
      cptBodyRegion?: string;
      cptLaterality?: string;
      cptContrast?: string;
      cptDetectionConfidence?: string;
      customTemplate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      return await reportService.generateReport({
        session_id: sessionId,
        clinical_history: clinicalHistory,
        patient_context: patientContext,
        cpt_code: cptCode,
        cpt_exam_name: cptExamName,
        cpt_detection_method: cptDetectionMethod,
        cpt_modality: cptModality,
        cpt_body_region: cptBodyRegion,
        cpt_laterality: cptLaterality,
        cpt_contrast: cptContrast,
        cpt_detection_confidence: cptDetectionConfidence,
        custom_template: customTemplate,
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const message = error?.response?.data?.detail || error?.message || 'Report generation failed';
      return rejectWithValue(message);
    }
  },
);

export const fetchReport = createAsyncThunk(
  'report/fetch',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      return await reportService.getReport(sessionId);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const message = error?.response?.data?.detail || error?.message || 'Failed to fetch report';
      return rejectWithValue(message);
    }
  },
);

export const regenerateReport = createAsyncThunk(
  'report/regenerate',
  async (
    { sessionId, clinicalHistory, patientContext, cptCode, cptExamName, cptDetectionMethod, cptModality, cptBodyRegion, cptLaterality, cptContrast, cptDetectionConfidence, customTemplate }: {
      sessionId: string;
      clinicalHistory?: string;
      patientContext?: PatientContextPayload;
      cptCode?: string;
      cptExamName?: string;
      cptDetectionMethod?: string;
      cptModality?: string;
      cptBodyRegion?: string;
      cptLaterality?: string;
      cptContrast?: string;
      cptDetectionConfidence?: string;
      customTemplate?: string;
    },
    { rejectWithValue, getState },
  ) => {
    // Frontend UX guard: Check cached config to prevent unnecessary API calls.
    // Backend remains authoritative - it will return 409 if hard-lock is enabled.
    const state = getState() as { report: ReportState };
    if (state.report.config?.hardLockEnabled && state.report.report?.status === 'final') {
      return rejectWithValue('Report is finalized and locked (hard-lock mode enabled)');
    }

    try {
      return await reportService.regenerateReport({
        session_id: sessionId,
        clinical_history: clinicalHistory,
        patient_context: patientContext,
        cpt_code: cptCode,
        cpt_exam_name: cptExamName,
        cpt_detection_method: cptDetectionMethod,
        cpt_modality: cptModality,
        cpt_body_region: cptBodyRegion,
        cpt_laterality: cptLaterality,
        cpt_contrast: cptContrast,
        cpt_detection_confidence: cptDetectionConfidence,
        custom_template: customTemplate,
      });
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const message = error?.response?.data?.detail || error?.message || 'Report regeneration failed';
      return rejectWithValue(message);
    }
  },
);

export const updateReport = createAsyncThunk(
  'report/update',
  async (
    { reportId, data }: { reportId: number; data: UpdateReportRequest },
    { rejectWithValue },
  ) => {
    try {
      return await reportService.updateReport(reportId, data);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const message = error?.response?.data?.detail || error?.message || 'Report update failed';
      return rejectWithValue(message);
    }
  },
);

export const finalizeReport = createAsyncThunk(
  'report/finalize',
  async (reportId: number, { rejectWithValue }) => {
    try {
      return await reportService.finalizeReport(reportId);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const message = error?.response?.data?.detail || error?.message || 'Report finalization failed';
      return rejectWithValue(message);
    }
  },
);

export const fetchReportConfig = createAsyncThunk(
  'report/fetchConfig',
  async (_, { rejectWithValue }) => {
    try {
      // Use apiClient (shared Axios instance) for consistency with all other
      // API calls — injects Bearer token and cache-control headers.
      const response = await apiClient.get<{
        hard_lock_enabled: boolean;
        export_theme: string;
        grading_enabled: boolean;
        grading_enforcement_mode: string;
      }>(
        '/api/v1/admin/public/report-config',
      );
      return response.data;
    } catch (err: unknown) {
      const error = err as { response?: { data?: { detail?: string } }; message?: string };
      const message = error?.response?.data?.detail || error?.message || 'Failed to fetch report configuration';
      return rejectWithValue(message);
    }
  }
);

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {
    setReport(state, action: PayloadAction<ReportResponse | null>) {
      state.report = action.payload;
      state.error = null;
      state.loading = false;
      state.isFinalized = action.payload?.status === 'final';
    },
    clearReport(state) {
      state.report = null;
      state.error = null;
      state.loading = false;
      state.isEditing = false;
      state.isSaving = false;
      state.isFinalized = false;
    },
    clearReportError(state) {
      state.error = null;
    },
    setEditing(state, action: PayloadAction<boolean>) {
      state.isEditing = action.payload;
    },
    setReportStatus(state, action: PayloadAction<string>) {
      if (state.report) {
        state.report.status = action.payload;
        state.isFinalized = action.payload === 'final';
      }
    },
    clearConfigError(state) {
      state.configError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateReport.fulfilled, (state, action: PayloadAction<ReportResponse>) => {
        state.loading = false;
        state.report = action.payload;
        state.isFinalized = action.payload.status === 'final';
      })
      .addCase(generateReport.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Report generation failed';
      })
      .addCase(fetchReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReport.fulfilled, (state, action: PayloadAction<ReportResponse>) => {
        state.loading = false;
        state.report = action.payload;
        state.isFinalized = action.payload.status === 'final';
      })
      .addCase(fetchReport.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Failed to fetch report';
      })
      .addCase(regenerateReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(regenerateReport.fulfilled, (state, action: PayloadAction<ReportResponse>) => {
        state.loading = false;
        state.report = action.payload;
        state.isFinalized = action.payload.status === 'final';
      })
      .addCase(regenerateReport.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || 'Report regeneration failed';
      })
      // ── Update report ──
      .addCase(updateReport.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateReport.fulfilled, (state, action: PayloadAction<ReportResponse>) => {
        state.isSaving = false;
        state.report = action.payload;
        state.isEditing = false;
        state.isFinalized = action.payload.status === 'final';
      })
      .addCase(updateReport.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) || 'Report update failed';
      })
      // ── Finalize report ──
      .addCase(finalizeReport.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(finalizeReport.fulfilled, (state, action: PayloadAction<ReportResponse>) => {
        state.isSaving = false;
        state.report = action.payload;
        state.isFinalized = true;
        state.isEditing = false;
      })
      .addCase(finalizeReport.rejected, (state, action) => {
        state.isSaving = false;
        state.error = (action.payload as string) || 'Report finalization failed';
      })
      // ── Fetch report config ──
      .addCase(fetchReportConfig.pending, () => {
        // Don't set loading to true for config fetch to avoid UI flicker
      })
      .addCase(fetchReportConfig.fulfilled, (state, action) => {
        state.config = {
          hardLockEnabled: action.payload.hard_lock_enabled,
          exportTheme: action.payload.export_theme,
          gradingEnabled: action.payload.grading_enabled ?? false,
          gradingEnforcementMode: action.payload.grading_enforcement_mode ?? 'warn',
        };
        state.configFetchedAt = Date.now();
      })
      .addCase(fetchReportConfig.rejected, (state, action) => {
        // Set configError (not error) to show non-blocking warning to user
        // while still allowing the UI to function with safe defaults.
        state.configError = (action.payload as string) || 'Failed to fetch report configuration';
        if (process.env.NODE_ENV === 'development') {
          console.warn('Failed to fetch report config:', action.error);
        }
        state.config = {
          hardLockEnabled: false,
          exportTheme: 'blue',
          gradingEnabled: false,
          gradingEnforcementMode: 'warn',
        };
        state.configFetchedAt = Date.now();
      });
  },
});

export const {
  setReport,
  clearReport,
  clearReportError,
  setEditing,
  setReportStatus,
  clearConfigError,
} = reportSlice.actions;

// Selectors
export const selectIsReportEditable = (state: { report: ReportState }): boolean => {
  const report = state.report.report;
  if (!report) return false;
  if (report.status !== 'final') return true;
  // Finalized reports are editable unless hard-lock mode is enabled
  const hardLockEnabled = state.report.config?.hardLockEnabled ?? false;
  return !hardLockEnabled;
};

// Config staleness check: returns true if config is older than maxAgeMs or never fetched.
// Accepts an explicit `now` timestamp (ms since epoch) so the selector is a pure function
// of its inputs — safe to memoize and will not cause spurious re-renders when used with
// useSelector (unlike calling Date.now() internally which always returns a new value).
export const selectIsConfigStale = (
  state: { report: ReportState },
  now: number,
  maxAgeMs: number = 5 * 60 * 1000,
): boolean => {
  const fetchedAt = state.report.configFetchedAt;
  if (!fetchedAt) return true;
  return now - fetchedAt > maxAgeMs;
};

// Combined selector that warns about stale config in console for debugging.
// Callers must pass the current timestamp; e.g. selectReportConfigWithFreshness(state, Date.now())
export const selectReportConfigWithFreshness = (state: { report: ReportState }, now: number) => {
  const isStale = selectIsConfigStale(state, now);
  if (isStale && state.report.configFetchedAt && process.env.NODE_ENV === 'development') {
    console.warn('Report config is stale; consider re-fetching to get latest admin settings');
  }
  return {
    config: state.report.config,
    isStale,
    fetchedAt: state.report.configFetchedAt,
  };
};

export default reportSlice.reducer;
